import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { IntegrationsMarketplaceAppContainerWebComponentSchema } from '../types';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { translateFn } from '../../../utils/translator.helper';
import {
  WEB_COMPONENT_INTEGRATIONS_MARKETPLACE_GRID_AVAILABLE,
  WEB_COMPONENT_INTEGRATIONS_MARKETPLACE_GRID_INSTALLED,
} from '../../../webcomponents.constants';
import { IntegrationsMarketplaceGridAvailableWebComponent } from '../../integrationsMarketplaceGridAvailable';
import { IntegrationsMarketplaceGridInstalledWebComponent } from '../../integrationsMarketplaceGridInstalled';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GridFiltersWebComponent } from '../../../grid/gridFilters';
import {
  Column,
  ColumnKeys,
  getColumnDetails,
  getDefaultFiltersGrids,
  PREDEFINED_FILTERS_KEYS,
} from '../types/filter-data';
import { IntegrationsMarketplaceGridFilterNirvanaDataProvider } from '../dataProvider/IntegrationsMarketplaceGridFilterNirvanaDataProvider';
import { CompanySelectorWebComponent } from '../../../companySelector';
import { SaveIntegrationWebComponent } from '../../saveIntegration';
import { GzTextWebComponent } from '../../../ui/gzText';
import {GzSideMenuWebComponent} from "../../../ui/gzSideMenu";
import { GzIconButtonWebComponent } from '../../../ui/gzIconButton';

export class IntegrationsMarketplaceAppContainerView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $title: GzTitleWebComponent;
    $gridFilters: GridFiltersWebComponent<Column, ColumnKeys>;
    $titleInstalled: GzTitleWebComponent;
    $titleAvailable: GzTitleWebComponent;
    $installedGrid: IntegrationsMarketplaceGridInstalledWebComponent;
    $availableGrid: IntegrationsMarketplaceGridAvailableWebComponent;
    $saveIntegration: SaveIntegrationWebComponent;
    $addIntegrationButton: GzButtonWebComponent;
    $headerBannerTitle: GzTitleWebComponent;
    $headerBannerText: GzTextWebComponent;
    $headerBannerButton: GzButtonWebComponent;
    $installedGridWrapper: HTMLElement;
    $emptyStateInstalledGrid: HTMLElement;
    $emptyStateInstalledGridTitle: GzTitleWebComponent;
    $emptyStateInstalledGridText: GzTextWebComponent;
    $sideMenu: GzSideMenuWebComponent;
    $gridDataRefresh: GzIconButtonWebComponent;
  };

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$title = this.shadow.querySelector('#main_title');
    this.dom.$gridFilters = this.shadow.querySelector('gz-grid-filters') as GridFiltersWebComponent<Column, ColumnKeys>;
    this.dom.$titleInstalled = this.shadow.querySelector('#installed_integrations') as GzTitleWebComponent;
    this.dom.$titleAvailable = this.shadow.querySelector('#available_integrations') as GzTitleWebComponent;
    this.dom.$installedGrid = this.shadow.querySelector(
      `${WEB_COMPONENT_INTEGRATIONS_MARKETPLACE_GRID_INSTALLED}`
    ) as IntegrationsMarketplaceGridInstalledWebComponent;

    this.dom.$availableGrid = this.shadow.querySelector(
      `${WEB_COMPONENT_INTEGRATIONS_MARKETPLACE_GRID_AVAILABLE}`
    ) as IntegrationsMarketplaceGridAvailableWebComponent;

    this.dom.$saveIntegration = this.shadow.querySelector('save-integration-wrapper') as SaveIntegrationWebComponent;

    this.dom.$addIntegrationButton = this.shadow.querySelector('#add-integration-button');
    this.dom.$headerBannerTitle = this.shadow.querySelector('#banner-title');
    this.dom.$headerBannerText = this.shadow.querySelector('#banner-text');
    this.dom.$headerBannerButton = this.shadow.querySelector('#banner-btn');

    this.dom.$installedGridWrapper = this.shadow.querySelector('.installed-grid-wrapper');

    this.dom.$emptyStateInstalledGrid = this.shadow.querySelector('.installed-grid-empty-state');
    this.dom.$emptyStateInstalledGridTitle = this.shadow.querySelector('#empty-state-installed-grid-title');
    this.dom.$emptyStateInstalledGridText = this.shadow.querySelector('#empty-state-installed-grid-text');

    this.dom.$sideMenu = this.shadow.querySelector('gz-side-menu');

    this.dom.$gridDataRefresh = this.shadow.getElementById('gridDataRefresh') as GzIconButtonWebComponent;

    this.setTranslatedStrings();
    this.buildGridFilters();
    this.injectDependenciesInChildren();
  }

  buildView(data: IntegrationsMarketplaceAppContainerWebComponentSchema) {
    this.toggleVisibilityEmptyStateInstalledGrid(false);

    if (!data) {
      this.toggleVisibilityEmptyStateInstalledGrid(true);
      this.dom.$wrapper.innerHTML = '';
      return;
    }
  }

  buildGridFilters() {
    const gridFilters = this.dom.$gridFilters;
    gridFilters.filters = getDefaultFiltersGrids(Application.Main.company.isPartner());
    gridFilters.allColumnDetails = getColumnDetails(Application.Main.company.isPartner());
    gridFilters.predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
    gridFilters.propertiesSet = true;
    gridFilters.dataProvider = new IntegrationsMarketplaceGridFilterNirvanaDataProvider();
  }

  setTranslatedStrings() {
    this.dom.$title.textContent = translateFn('Integrations hub');
    this.dom.$titleInstalled.textContent = translateFn('Configured');
    this.dom.$titleAvailable.textContent = translateFn('Integrations catalog');
    this.dom.$headerBannerTitle.textContent = translateFn('Enhance your cybersecurity');
    this.dom.$headerBannerText.textContent = translateFn(
      'The Integrations page offers tools to manage both active and available integrations, giving you control over your configurations to enhance and monitor your cybersecurity setup.'
    );
    this.dom.$headerBannerButton.textContent = translateFn('Learn more');
    this.dom.$emptyStateInstalledGridTitle.textContent = translateFn('Start adding integrations');
    this.dom.$emptyStateInstalledGridText.textContent = translateFn("You don't have any integrations here yet.");
  }

  injectDependenciesInChildren() {
    if (this.dom.$availableGrid.injectDependencies) {
      this.dom.$availableGrid.injectDependencies(this.dependencies);
      this.dom.$availableGrid.start().catch(console.error);
    }

    if (this.dom.$installedGrid.injectDependencies) {
      this.dom.$installedGrid.injectDependencies(this.dependencies);
      this.dom.$installedGrid.start().catch(console.error);
    }

    if (this.dom.$gridFilters.injectDependencies) {
      this.dom.$gridFilters.injectDependencies(this.dependencies);
      this.dom.$gridFilters.start().catch(console.error);
    }
  }

  toggleVisibilityEmptyStateInstalledGrid(isVisible: boolean) {
    this.dom.$emptyStateInstalledGrid.classList.toggle('hidden', !isVisible);
    this.dom.$installedGridWrapper.classList.toggle('hidden', isVisible);
  }
}
